import React from 'react';
import { BarChart2, Goal, AlertTriangle, Clock, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';

const formatMMSS = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const StatTile = ({ icon, label, value, accent = '#00BFFF', testId }) => (
  <div data-testid={testId} className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4">
    <div className="flex items-center gap-2 mb-3" style={{ color: accent }}>
      {icon}
      <span className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">{label}</span>
    </div>
    <p className="text-3xl font-black text-white">{value}</p>
  </div>
);

export default function StatsView({ matchHistory, BottomNav }) {
  const totals = matchHistory.reduce((acc, m) => {
    const events = m.matchState.events || [];
    acc.goals += (m.matchState.homeScore || 0) + (m.matchState.awayScore || 0);
    acc.yellow += events.filter(e => e.type === 'yellow').length;
    acc.red += events.filter(e => e.type === 'red').length;
    acc.sinBin += events.filter(e => e.type === 'sin_bin').length;
    acc.added += (m.totalAddedTime || 0);
    return acc;
  }, { goals: 0, yellow: 0, red: 0, sinBin: 0, added: 0 });

  const matchCount = matchHistory.length;
  const avgGoals = matchCount ? (totals.goals / matchCount).toFixed(1) : '0.0';
  const avgAdded = matchCount ? Math.round(totals.added / matchCount) : 0;

  const chartData = [...matchHistory]
    .slice(0, 8)
    .reverse()
    .map((m, i) => ({
      name: `${(m.config.homeTeam || '?').slice(0, 3).toUpperCase()}–${(m.config.awayTeam || '?').slice(0, 3).toUpperCase()}`,
      goals: (m.matchState.homeScore || 0) + (m.matchState.awayScore || 0),
      idx: i,
    }));

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A] text-white">
      <div className="p-6 border-b border-gray-800 flex items-center gap-3">
        <BarChart2 className="w-5 h-5 text-[#00BFFF]" />
        <h1 className="text-xl font-bold tracking-wider">STATISTICS</h1>
      </div>

      <div className="flex-1 p-5 space-y-5 overflow-y-auto">
        {matchCount === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <BarChart2 className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <p className="text-sm">No stats yet. Referee a match to see your analytics.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <StatTile testId="stat-tile-matches" icon={<TrendingUp size={16} />} label="Matches" value={matchCount} />
              <StatTile testId="stat-tile-goals" icon={<Goal size={16} />} label="Total Goals" value={totals.goals} />
              <StatTile testId="stat-tile-avg-goals" icon={<Goal size={16} />} label="Goals / Match" value={avgGoals} accent="#FFD400" />
              <StatTile testId="stat-tile-avg-added" icon={<Clock size={16} />} label="Avg Added" value={formatMMSS(avgAdded)} accent="#FFD400" />
            </div>

            {/* Cards breakdown */}
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4 text-[#FF3B30]">
                <AlertTriangle size={16} />
                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Discipline</span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="w-5 h-7 bg-[#FFD400] rounded-sm mx-auto mb-2" />
                  <p data-testid="stat-yellow" className="text-2xl font-black">{totals.yellow}</p>
                  <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-1">Yellow</p>
                </div>
                <div>
                  <div className="w-5 h-7 bg-[#FF3B30] rounded-sm mx-auto mb-2" />
                  <p data-testid="stat-red" className="text-2xl font-black">{totals.red}</p>
                  <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-1">Red</p>
                </div>
                <div>
                  <div className="w-5 h-7 bg-[#00D26A] rounded-sm mx-auto mb-2" />
                  <p data-testid="stat-sinbin" className="text-2xl font-black">{totals.sinBin}</p>
                  <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-1">Sin Bin</p>
                </div>
              </div>
            </div>

            {/* Goals per match chart */}
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-5">
              <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-4">Goals Per Match</p>
              <div style={{ width: '100%', height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      contentStyle={{ background: '#121212', border: '1px solid #374151', borderRadius: 12, fontSize: 12 }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="goals" radius={[6, 6, 0, 0]}>
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill="#00BFFF" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
      {BottomNav}
    </div>
  );
}
